import { useState, useEffect, useMemo } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import VuiBadge from "components/VuiBadge";
import GradientBorder from "examples/GradientBorder";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

import radialGradient from "assets/theme/functions/radialGradient";
import palette from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";

import { getExpenses, updateExpense, deleteExpense, addExpense } from "services/api";

const CATEGORIES = [
  "Food","Fuel","Groceries","Shopping","ATM/Cash","Transfer",
  "Utilities","Entertainment","Medical","Office","Subscription",
  "Advertising","Education","Income","Other",
];

function Transactions() {
  const [expenses, setExpenses] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("");
  const [filterDir, setFilterDir] = useState("");
  const [filterCur, setFilterCur] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [isNew, setIsNew] = useState(false);

  const load = async () => {
    const data = await getExpenses();
    if (data) { setExpenses(data); setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    let f = [...expenses];
    if (filterCat) f = f.filter((e) => e.category === filterCat);
    if (filterDir) f = f.filter((e) => (e.direction || "debit") === filterDir);
    if (filterCur) f = f.filter((e) => (e.currency || "PKR") === filterCur);
    setFiltered(f);
  }, [expenses, filterCat, filterDir, filterCur]);

  const openEdit = (txn) => {
    setEditData({
      id: txn.id, amount: txn.amount, currency: txn.currency || "PKR",
      category: txn.category || "Other", direction: txn.direction || "debit",
      vendor: txn.vendor || "", description: txn.description || "",
    });
    setIsNew(false);
    setEditOpen(true);
  };

  const openAdd = () => {
    setEditData({
      id: null, amount: "", currency: "PKR", category: "Other",
      direction: "debit", vendor: "", description: "",
      timestamp: new Date().toISOString().split("T")[0],
    });
    setIsNew(true);
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (isNew) {
      const payload = { ...editData };
      if (payload.timestamp) payload.timestamp = payload.timestamp + "T12:00:00+05:00";
      delete payload.id;
      await addExpense(payload);
    } else {
      const { id, ...rest } = editData;
      await updateExpense(id, rest);
    }
    setEditOpen(false);
    load();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this transaction?")) {
      await deleteExpense(id);
      load();
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

  const selectSx = {
    backgroundColor: "rgba(15,21,53,0.8)",
    color: "#fff",
    border: "1px solid rgba(226,232,240,0.15)",
    borderRadius: "10px",
    padding: "8px 12px",
    fontSize: "13px",
    fontFamily: "inherit",
    outline: "none",
    cursor: "pointer",
    appearance: "auto",
    minWidth: "120px",
  };

  const columns = [
    { name: "date", align: "left" },
    { name: "description", align: "left" },
    { name: "vendor", align: "left" },
    { name: "category", align: "center" },
    { name: "type", align: "center" },
    { name: "amount", align: "right" },
    { name: "actions", align: "center" },
  ];

  const rows = useMemo(() =>
    filtered.map((txn) => ({
      date: (
        <VuiTypography variant="caption" color="white" fontWeight="medium">
          {(txn.timestamp || "").slice(0, 10)}
        </VuiTypography>
      ),
      description: (
        <VuiTypography variant="caption" color="text" fontWeight="regular" sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
          {(txn.description || "-").slice(0, 50)}
        </VuiTypography>
      ),
      vendor: (
        <VuiTypography variant="caption" color="white" fontWeight="medium">
          {(txn.vendor || "-").slice(0, 25)}
        </VuiTypography>
      ),
      category: (
        <VuiBadge
          variant="standard"
          badgeContent={txn.category || "Other"}
          color="info"
          size="xs"
          container
          sx={({ palette: { white, info }, borders: { borderRadius, borderWidth } }) => ({
            background: info.main,
            border: `${borderWidth[1]} solid ${info.main}`,
            borderRadius: borderRadius.md,
            color: white.main,
          })}
        />
      ),
      type: (
        <VuiBadge
          variant="standard"
          badgeContent={(txn.direction || "debit") === "credit" ? "Income" : "Expense"}
          size="xs"
          container
          sx={({ palette: { white, success }, borders: { borderRadius, borderWidth } }) => ({
            background: (txn.direction || "debit") === "credit" ? success.main : "rgba(231,76,60,0.6)",
            border: `${borderWidth[1]} solid ${(txn.direction || "debit") === "credit" ? success.main : "rgba(231,76,60,0.6)"}`,
            borderRadius: borderRadius.md,
            color: white.main,
          })}
        />
      ),
      amount: (
        <VuiTypography variant="button" fontWeight="bold" color={(txn.direction || "debit") === "credit" ? "success" : "white"}>
          {(txn.direction || "debit") === "credit" ? "+" : ""}{txn.currency || "PKR"} {fmt(txn.amount)}
        </VuiTypography>
      ),
      actions: (
        <VuiBox display="flex" gap="8px" justifyContent="center">
          <VuiTypography
            component="a"
            href="#"
            variant="caption"
            color="text"
            fontWeight="medium"
            onClick={(e) => { e.preventDefault(); openEdit(txn); }}
            sx={{ cursor: "pointer", "&:hover": { color: "#0075ff" } }}
          >
            Edit
          </VuiTypography>
          <VuiTypography
            component="a"
            href="#"
            variant="caption"
            color="text"
            fontWeight="medium"
            onClick={(e) => { e.preventDefault(); handleDelete(txn.id); }}
            sx={{ cursor: "pointer", "&:hover": { color: "#e74c3c" } }}
          >
            Delete
          </VuiTypography>
        </VuiBox>
      ),
    })),
    [filtered]
  );

  const dialogSx = {
    "& .MuiDialog-paper": {
      background: "linear-gradient(127deg, rgb(6,11,40) 28%, rgb(10,14,35) 91%)",
      borderRadius: "20px",
      border: "1.5px solid rgba(226,232,240,0.15)",
      color: "#fff",
      minWidth: "420px",
      boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
    },
  };

  const inputBorder = radialGradient(
    palette.gradients.borderLight.main,
    palette.gradients.borderLight.state,
    palette.gradients.borderLight.angle
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <Card>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb="22px" flexWrap="wrap" gap="12px">
              <VuiTypography variant="lg" color="white">
                All Transactions
              </VuiTypography>
              <VuiBox display="flex" gap="10px" alignItems="center" flexWrap="wrap">
                <select style={selectSx} value={filterDir} onChange={(e) => setFilterDir(e.target.value)}>
                  <option value="">All Types</option>
                  <option value="debit">Expenses</option>
                  <option value="credit">Income</option>
                </select>
                <select style={selectSx} value={filterCat} onChange={(e) => setFilterCat(e.target.value)}>
                  <option value="">All Categories</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select style={selectSx} value={filterCur} onChange={(e) => setFilterCur(e.target.value)}>
                  <option value="">All Currencies</option>
                  <option value="PKR">PKR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                </select>
                <VuiButton color="info" size="small" onClick={openAdd}>
                  + Add Entry
                </VuiButton>
              </VuiBox>
            </VuiBox>
            <VuiBox
              sx={{
                "& th": {
                  borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                    `${borderWidth[1]} solid ${grey[700]}`,
                },
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                      `${borderWidth[1]} solid ${grey[700]}`,
                  },
                },
              }}
            >
              {loading ? (
                <VuiBox p={3}>
                  <VuiTypography color="text" variant="button">Loading transactions...</VuiTypography>
                </VuiBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </VuiBox>
          </Card>
        </VuiBox>
      </VuiBox>

      {/* Edit/Add Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} sx={dialogSx}>
        <DialogTitle>
          <VuiTypography variant="lg" color="white" fontWeight="bold">
            {isNew ? "Add Transaction" : "Edit Transaction"}
          </VuiTypography>
        </DialogTitle>
        <DialogContent>
          <VuiBox display="flex" flexDirection="column" gap="16px" mt={1}>
            <VuiBox>
              <VuiBox mb={0.5} ml={0.5}>
                <VuiTypography component="label" variant="caption" color="white" fontWeight="bold">
                  Amount
                </VuiTypography>
              </VuiBox>
              <GradientBorder minWidth="100%" padding="1px" borderRadius={borders.borderRadius.lg} backgroundImage={inputBorder}>
                <VuiInput type="number" placeholder="Amount" value={editData.amount || ""}
                  onChange={(e) => setEditData({ ...editData, amount: e.target.value })} />
              </GradientBorder>
            </VuiBox>
            <VuiBox>
              <VuiBox mb={0.5} ml={0.5}>
                <VuiTypography component="label" variant="caption" color="white" fontWeight="bold">
                  Currency
                </VuiTypography>
              </VuiBox>
              <select style={{ ...selectSx, width: "100%" }} value={editData.currency || "PKR"}
                onChange={(e) => setEditData({ ...editData, currency: e.target.value })}>
                {["PKR","USD","GBP","EUR"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </VuiBox>
            <VuiBox>
              <VuiBox mb={0.5} ml={0.5}>
                <VuiTypography component="label" variant="caption" color="white" fontWeight="bold">
                  Category
                </VuiTypography>
              </VuiBox>
              <select style={{ ...selectSx, width: "100%" }} value={editData.category || "Other"}
                onChange={(e) => setEditData({ ...editData, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </VuiBox>
            <VuiBox>
              <VuiBox mb={0.5} ml={0.5}>
                <VuiTypography component="label" variant="caption" color="white" fontWeight="bold">
                  Type
                </VuiTypography>
              </VuiBox>
              <select style={{ ...selectSx, width: "100%" }} value={editData.direction || "debit"}
                onChange={(e) => setEditData({ ...editData, direction: e.target.value })}>
                <option value="debit">Expense</option>
                <option value="credit">Income</option>
              </select>
            </VuiBox>
            <VuiBox>
              <VuiBox mb={0.5} ml={0.5}>
                <VuiTypography component="label" variant="caption" color="white" fontWeight="bold">
                  Vendor
                </VuiTypography>
              </VuiBox>
              <GradientBorder minWidth="100%" padding="1px" borderRadius={borders.borderRadius.lg} backgroundImage={inputBorder}>
                <VuiInput placeholder="Vendor name" value={editData.vendor || ""}
                  onChange={(e) => setEditData({ ...editData, vendor: e.target.value })} />
              </GradientBorder>
            </VuiBox>
            <VuiBox>
              <VuiBox mb={0.5} ml={0.5}>
                <VuiTypography component="label" variant="caption" color="white" fontWeight="bold">
                  Description
                </VuiTypography>
              </VuiBox>
              <GradientBorder minWidth="100%" padding="1px" borderRadius={borders.borderRadius.lg} backgroundImage={inputBorder}>
                <VuiInput placeholder="Description" value={editData.description || ""}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })} />
              </GradientBorder>
            </VuiBox>
            {isNew && (
              <VuiBox>
                <VuiBox mb={0.5} ml={0.5}>
                  <VuiTypography component="label" variant="caption" color="white" fontWeight="bold">
                    Date
                  </VuiTypography>
                </VuiBox>
                <GradientBorder minWidth="100%" padding="1px" borderRadius={borders.borderRadius.lg} backgroundImage={inputBorder}>
                  <VuiInput type="date" value={editData.timestamp || ""}
                    onChange={(e) => setEditData({ ...editData, timestamp: e.target.value })} />
                </GradientBorder>
              </VuiBox>
            )}
          </VuiBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <VuiButton color="light" variant="outlined" onClick={() => setEditOpen(false)}>
            Cancel
          </VuiButton>
          <VuiButton color="info" onClick={handleSave}>
            Save
          </VuiButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Transactions;
